import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';
import { Form } from '@plone/volto/components/manage/Form';
import { submitForm } from '@plone/volto-form-block/actions/form';
import ThankYou from '@plone/volto-form-block/components/ThankYou/ThankYou';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { tryParseJSON } from '@plone/volto/helpers/FormValidation/FormValidation';
import { extractInvariantErrors } from '@plone/volto/helpers/FormValidation/FormValidation';
import { toast } from 'react-toastify';
import Toast from '@plone/volto/components/manage/Toast/Toast';
import { useLocation } from 'react-router-dom';
import qs from 'query-string';
import {
  flatten,
  includes,
  keys,
  map,
  omitBy,
  pickBy,
  without,
  isObject,
  isString,
  isBoolean,
  isUndefined,
  fromPairs,
} from 'lodash';
import config from '@plone/volto/registry';
import { renderToString } from 'react-dom/server';
import { stripRequiredProperty } from '@plone/volto-form-block/helpers/schema';
import {
  formatShortDate,
  formatLongDateTime,
} from '@plone/volto-form-block/helpers/date';
/* START CUSTOMIZATION */
import { Info } from '@designsystem/stories/02-atoms/Info';
import { submitEventRegistrationForm } from '@fhnw/actions/submitEventRegistrationForm';
import { useOccasionRegistration } from '@fhnw/components/OccasionRegistrationPanel';
/* END CUSTOMIZATION */

const messages = defineMessages({
  error: {
    id: 'Error',
    defaultMessage: 'Error',
  },
  submit: {
    id: 'Submit',
    defaultMessage: 'Submit',
  },
  errorSend: {
    id: 'Your form could not be sent. Please reload the page and try again. The data you entered will be retained.',
    defaultMessage:
      'Your form could not be sent. Please reload the page and try again. The data you entered will be retained.',
  },
  errorCaptcha: {
    id: 'Your form could not be sent. Please try again later.',
    defaultMessage: 'Your form could not be sent. Please try again later.',
  },
  cancel: {
    id: 'Cancel',
    defaultMessage: 'Cancel',
  },
  yes: {
    id: 'Yes',
    defaultMessage: 'Yes',
  },
  no: {
    id: 'No',
    defaultMessage: 'No',
  },
  requiredCheckbox: {
    id: 'requiredCheckbox',
    defaultMessage: '{field} is required to be checked.',
  },
});

interface FormBlockViewProps {
  data: Record<string, any>;
  id: string;
  path: string;
}

const FormBlockView = ({ data, id, path }: FormBlockViewProps) => {
  /* START CUSTOMIZATION */
  const ref = useRef<HTMLDivElement>(null);
  /* END CUSTOMIZATION */
  const dispatch = useDispatch();
  const intl = useIntl();
  const location = useLocation();
  const [submitted, setSubmitted] = useState(false);
  const [submitPressed, setSubmitPressed] = useState(false);
  const [submittedData, setSubmittedData] = useState<Record<string, any>>({});
  /* START CUSTOMIZATION */
  // Derive instead of mutating the block data in place.
  const schema = stripRequiredProperty(data.schema);
  /* END CUSTOMIZATION */
  const lang = useSelector(
    (state: { intl: { locale: string } }) => state.intl.locale,
  );
  /* START CUSTOMIZATION */
  const { occasionUid, hidden } = useOccasionRegistration();
  /* END CUSTOMIZATION */

  const schemaFormConfig = config.blocks.blocksConfig.schemaForm as any;

  const propertyNames = keys(schema.properties);
  const queryParams = qs.parse(location.search);

  /* START CUSTOMIZATION */
  // Persistence policy for the localStorage draft: never keep values the user
  // cannot see or change, and drop keys whose field vanished from the schema
  // (stale drafts would otherwise crash on `field.widget`).
  const getFieldDef = (key: string) => schema?.properties?.[key];

  const shouldPersistKey = (key: string) => {
    const field = getFieldDef(key);
    if (!field) return false;
    if (field.widget === 'static_text') return false;
    if (field.factory === 'static_text') return false;
    if (field.factory === 'hidden') return false;
    // File uploads arrive as base64 blobs: persisting them on every change
    // blows the localStorage quota (QuotaExceededError with a 6 MB CV) and
    // would park uploaded documents in the browser storage.
    if (field.factory === 'File Upload') return false;
    if (field.widget === 'file') return false;
    if (key === 'captchaWidget') return false;

    return true;
  };

  const filterPersistableFormData = (formData: Record<string, any>) =>
    Object.fromEntries(
      Object.entries(formData || {}).filter(([key]) => shouldPersistKey(key)),
    );

  const readFormBlocks = (): Record<string, any> => {
    try {
      return JSON.parse(localStorage.getItem('formBlocks') || '') || {};
    } catch {
      return {};
    }
  };

  const writeFormBlocks = (value: Record<string, any>) => {
    // Upstream guards this call too: a quota error must not escape from
    // Form's change handler, the draft is a convenience only.
    try {
      localStorage.setItem('formBlocks', JSON.stringify(value));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Form draft not saved to localStorage', e);
    }
  };
  /* END CUSTOMIZATION */

  let initialData: Record<string, any> = {};

  /* START CUSTOMIZATION */
  const [localStorageData] = useState<Record<string, any>>(() => {
    try {
      return readFormBlocks();
    } catch {
      return {};
    }
  });

  if (localStorageData[id]) {
    initialData = filterPersistableFormData(localStorageData[id]);
  }

  const [captchaErrors] = useState<Record<string, any>>(() => {
    try {
      return JSON.parse(localStorage.getItem('formBlocksError') || '') || {};
    } catch {
      return {};
    }
  });

  const captchaError = captchaErrors[id] === 'error';
  /* END CUSTOMIZATION */

  useEffect(() => {
    if (captchaError) {
      toast.error(
        <Toast
          error
          title={intl.formatMessage(messages.errorCaptcha)}
          content=""
        />,
        /* START CUSTOMIZATION */
        { toastId: `captcha-${id}` },
        /* END CUSTOMIZATION */
      );
    }
  }, [captchaError, intl, id]);

  propertyNames.forEach((property) => {
    if (queryParams[property] !== undefined) {
      initialData[property] = queryParams[property];
    }

    const field = schema.properties[property];

    if (
      field.factory &&
      field.factory === 'checkbox_group' &&
      isString(field.default)
    ) {
      initialData[property] = field.default.split('\n');
    }

    const queryParameterName = field.queryParameterName;

    if (
      queryParameterName !== undefined &&
      queryParams[queryParameterName] !== undefined
    ) {
      initialData[property] = queryParams[queryParameterName];
    }
  });

  /* START CUSTOMIZATION */
  // Event-registration only: prefill the 4 hidden fields with the active
  // occasion's display values. The occasion (from the B5 panel context) is
  // authoritative, so this runs after the generic query-param logic above.
  if (data.eventRegistration && occasionUid && hidden) {
    (
      ['eventname', 'durchfuehrung', 'datum', 'durchfuehrungsort'] as const
    ).forEach((k) => {
      if (hidden[k] != null) initialData[k] = hidden[k];
    });
  }
  /* END CUSTOMIZATION */

  const onCancel = () => {};

  const onChangeFormData = (formData: Record<string, any>) => {
    /* START CUSTOMIZATION */
    // Persist every change. Volto's Form only calls this when formData really
    // changed, so there is no mount-time call to skip: a form without hidden
    // fields or a captcha widget would otherwise lose the user's first input
    // (#1756 review). When a widget does write on mount, formData already
    // carries the restored draft (it is this component's initialData).
    let stored: Record<string, any> = {};
    try {
      stored = readFormBlocks();
    } catch {
      stored = {};
    }

    const storeData = filterPersistableFormData(formData);

    writeFormBlocks({ ...stored, [id]: storeData });
    /* END CUSTOMIZATION */
  };

  const onSubmit = async (formData: Record<string, any>) => {
    let submitData = { ...formData };
    const captcha: { provider: any; props: any; token?: string } = {
      provider: data.captcha,
      props: data.captcha_props || {},
    };
    if (data.captcha === 'recaptcha') {
      captcha.token = await submitData.captchaWidget();
      delete submitData.captchaWidget;
    }

    setSubmitPressed(true);

    submitData = pickBy(
      submitData,
      (value, field) =>
        !includes(
          config.blocks.blocksConfig.schemaForm.filterFactorySend,
          schema.properties[field]?.factory,
        ),
    );

    const requiredErrors: string[] = [];

    map(keys(submitData), (field) => {
      if (
        schema.properties[field]?.factory === 'number' &&
        submitData[field] !== undefined
      ) {
        submitData[field] = parseInt(submitData[field]);
      }

      if (
        schema.properties[field]?.factory === 'time' &&
        isObject(submitData[field])
      ) {
        const time = submitData[field] as { hour: number; minute: number };
        submitData[field] = `${time.hour}:${time.minute}`;
      }

      if (
        schema.properties[field]?.factory === 'label_boolean_field' ||
        schema.properties[field]?.type === 'boolean'
      ) {
        if (!isBoolean(submitData[field])) {
          submitData[field] = false;
        }
        if (!submitData[field] && schema.required?.includes(field)) {
          requiredErrors.push(
            intl.formatMessage(messages.requiredCheckbox, {
              field: schema.properties[field].title,
            }),
          );
        }
      }
    });

    if (requiredErrors.length > 0) {
      map(requiredErrors, (title) => {
        toast.error(<Toast error title={title} content="" />);
      });
      setSubmitPressed(false);
      return;
    }

    // Order fields based on schema
    submitData = fromPairs(
      flatten(
        map(schema.fieldsets, (fieldset: { fields: string[] }) =>
          map(fieldset.fields, (field) => [field, submitData[field]]),
        ),
      ),
    );

    // Remove empty values
    submitData = omitBy(submitData, (value) => value === null);

    /* START CUSTOMIZATION */
    // Event-registration with an active occasion submits via the occasion-carrying
    // action (B3) so the backend email override (B1) fires; everything else keeps
    // the stock submitForm path unchanged.
    const action =
      data.eventRegistration && occasionUid
        ? submitEventRegistrationForm(
            path,
            id,
            submitData,
            captcha,
            occasionUid,
          )
        : submitForm(path, id, submitData, captcha);
    /* END CUSTOMIZATION */

    // submitForm is a thunk that resolves once the form is submitted; the base
    // redux Dispatch type doesn't surface the returned promise.
    (dispatch(action) as unknown as Promise<any>)
      .then(() => {
        // Set submitted
        setSubmitted(true);
        setSubmittedData(submitData);

        // Clear error
        let errors: Record<string, any> = {};
        try {
          errors =
            JSON.parse(localStorage.getItem('formBlocksError') || '') || {};
        } catch (error) {
          errors = {};
        }

        delete errors[id];
        /* START CUSTOMIZATION */
        // Upstream writes the error map to `formBlocks` and the draft map to
        // `formBlocksError` — the two keys are swapped there, which wipes the
        // draft store and leaves stale captcha errors behind.
        localStorage.setItem('formBlocksError', JSON.stringify(errors));

        // Clear localstorage
        let saved: Record<string, any> = {};
        try {
          saved = readFormBlocks();
        } catch {
          saved = {};
        }
        delete saved[id];
        writeFormBlocks(saved);
        /* END CUSTOMIZATION */

        // Redirect after submit
        if (
          Array.isArray(data.forward_user_to) &&
          data.forward_user_to.length > 0
        ) {
          window.location.href = flattenToAppURL(
            data.forward_user_to[0]['@id'],
          );
        } else {
          const url = new URL(window.location.href);
          url.searchParams.set('send', 'true');
          /* START CUSTOMIZATION */
          /* eslint-disable */
          if (typeof history !== 'undefined') {
            history.pushState({}, '', url);
          }
          /* eslint-enable */
          /* END CUSTOMIZATION */
        }
      })
      .catch((err) => {
        let message =
          err?.response?.body?.error?.message ||
          err?.response?.body?.message ||
          err?.response?.text ||
          '';
        const errorsList = tryParseJSON(message);
        let invariantErrors: string[] = [];
        if (Array.isArray(errorsList)) {
          invariantErrors = extractInvariantErrors(errorsList);
        }

        // Clear error
        let errors: Record<string, any> = {};
        try {
          errors =
            JSON.parse(localStorage.getItem('formBlocksError') || '') || {};
        } catch (error) {
          errors = {};
        }

        errors[id] = 'error';
        localStorage.setItem('formBlocksError', JSON.stringify(errors));

        if (
          err.status === 400 &&
          (err?.response?.body?.message === 'Kein Captcha-Token angegeben.' ||
            err?.response?.body?.error?.message ===
              'No captcha token provided.')
        ) {
          window.location.reload();
        }

        if (invariantErrors.length > 0) {
          toast.error(
            <Toast
              error
              title={intl.formatMessage(messages.error)}
              content={invariantErrors.join(' - ')}
            />,
            {
              autoClose: false,
              /* START CUSTOMIZATION */
              toastId: `captcha-${id}`,
              /* END CUSTOMIZATION */
            },
          );
        } else {
          toast.error(
            <Toast
              error
              title={intl.formatMessage(messages.errorSend)}
              content={message}
            />,
            {
              autoClose: false,
              /* START CUSTOMIZATION */
              toastId: `captcha-${id}`,
              /* END CUSTOMIZATION */
            },
          );
        }

        setSubmitPressed(false);
      });
  };

  const formatProperty = (factory: string, value: any) => {
    switch (factory) {
      case 'termsAccepted':
      case 'label_boolean_field':
        return value === true
          ? intl.formatMessage(messages.yes)
          : intl.formatMessage(messages.no);
      case 'checkbox_group':
        return Array.isArray(value)
          ? value.map((item, i) => [item, <br key={i} />]).flat()
          : value;
      case 'label_date_field':
        return isUndefined(value) ? '' : formatShortDate(value, lang);
      case 'label_datetime_field':
        return isUndefined(value) ? '' : formatLongDateTime(value, lang);
      default:
        return value;
    }
  };

  /* START CUSTOMIZATION */
  // Upstream renders the submitted values as a semantic-ui Grid; the FHNW
  // design uses the designsystem Info list instead.
  const info: { key: string; value: any }[] = [];
  map(keys(submittedData), (property) => {
    const propertyType = schema.properties[property].type;

    if (
      propertyType !== 'object' &&
      property !== 'captchaWidget' &&
      schema.properties[property].factory !== 'hidden'
    ) {
      info.push({
        key: schema.properties[property].title,
        value: formatProperty(
          schema.properties[property].factory,
          submittedData[property],
        ),
      });
    }
  });

  const formfields = renderToString(<Info type="extended" items={info} />);

  useEffect(() => {
    window.requestAnimationFrame(() => {
      if (submitted && ref.current) {
        ref.current.scrollIntoView();
      }
    });
  }, [submitted]);
  /* END CUSTOMIZATION */

  return (
    /* START CUSTOMIZATION */
    <div ref={ref} className="block schemaForm">
      {data.title && <h3>{data.title}</h3>}
      {data.description && <p>{data.description}</p>}
      {/* END CUSTOMIZATION */}
      {submitted ? (
        <ThankYou
          data={data}
          formfields={formfields}
          submittedData={submittedData}
        />
      ) : (
        <Form
          /* START CUSTOMIZATION */
          key={location.search}
          /* END CUSTOMIZATION */
          schema={{
            ...schema,
            fieldsets: map(
              schema.fieldsets,
              (fieldset: { fields: string[] }) => ({
                ...fieldset,
                fields: includes(fieldset.fields, 'captchaWidget')
                  ? [
                      ...without(fieldset.fields, 'captchaWidget'),
                      'captchaWidget',
                    ]
                  : fieldset.fields,
              }),
            ),
          }}
          formData={initialData}
          widgets={schemaFormConfig.widgets}
          component={schemaFormConfig.component}
          buttonComponent={schemaFormConfig.buttonComponent}
          onSubmit={submitPressed ? () => {} : onSubmit}
          resetOnCancel={true}
          onChangeFormData={onChangeFormData}
          onCancel={data.show_cancel ? onCancel : null}
          submitLabel={data.submit_label || intl.formatMessage(messages.submit)}
          cancelLabel={data.cancel_label || intl.formatMessage(messages.cancel)}
          textButtons={true}
        />
      )}
    </div>
  );
};

export default FormBlockView;
